// /src/components/BillingComponent.js
import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { updateCompanyInfo } from "../store/actions/billingActions";
import {
  Typography,
  TextField,
  Button,
  Grid,
  Box,
  Paper,
  Chip,
} from "@mui/material";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import GlobalMessage from "./GlobalMessage";
import { useTranslation } from "react-i18next";

const ERR_MSG_UPDATE = "Failed to update billing information.";

const CompanyInfoSchema = Yup.object().shape({
  companyName: Yup.string().required("Company Name is required"),
  address: Yup.string().required("Address is required"),
  regNr: Yup.string().required("Registry number is required"),
  vatNr: Yup.string().required("VAT Number is required"),
  city: Yup.string().required("City is required"),
  areaCode: Yup.string().required("Area Code is required"),
  emailAddress: Yup.string()
    .email("Invalid email")
    .required("Email Address is required"),
});

const BillingComponent = () => {
  const dispatch = useDispatch();
  const [successMessage, setSuccessMessage] = useState("");
  const [fetchError, setFetchError] = useState("");
  const [openSnackbar, setOpenSnackbar] = useState(false);

  const accountType = useSelector((state) => state.billing.accountType) || "";
  const { t } = useTranslation(); // Get the t function

  // TODO: remove default information after testing
  const companyInfo = useSelector((state) => state.billing.companyInfo) || {
    companyName: "",
    address: "",
    regNr: "",
    vatNr: "",
    city: "",
    areaCode: "",
    emailAddress: "",
  };

  const handleSubmit = (values, { setSubmitting }) => {
    dispatch(updateCompanyInfo(values))
      .then(() => {
        setSuccessMessage("Company information updated.");
        setSubmitting(false);
        setOpenSnackbar(true);
      })
      .catch((error) => {
        setSubmitting(false);
        setFetchError(ERR_MSG_UPDATE);
        setOpenSnackbar(true);
      });
  };

  const handleCloseSnackbar = () => {
    setFetchError("");
    setSuccessMessage("");
    setOpenSnackbar(false);
  };

  return (
    <Box 
      p={3}
      sx={{
        maxWidth: "800pt",
        mx: "auto",
        width: "100%"
    }}>
      {/* Company Information Form */}
      <Paper elevation={3} sx={{ padding: 2, marginBottom: 4 }}>
        <Typography variant="h5" gutterBottom>
          {t("billing.companyInformation")}
        </Typography>
        <Formik
          initialValues={companyInfo}
          validationSchema={CompanyInfoSchema}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({
            values,
            handleChange,
            handleBlur,
            isSubmitting,
            touched,
            errors,
          }) => (
            <Form>
              <Grid container spacing={2}>
                {/* Company Name */}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Company Name"
                    name="companyName"
                    value={values.companyName}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.companyName && Boolean(errors.companyName)}
                    helperText={touched.companyName && errors.companyName}
                  />
                </Grid>

                {/* Address */}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label={t("billing.address")}
                    name="address"
                    value={values.address}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.address && Boolean(errors.address)}
                    helperText={touched.address && errors.address}
                  />
                </Grid>

                {/* Reg Number */}
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label={t("billing.regNr")}
                    name="regNr"
                    value={values.regNr}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.regNr && Boolean(errors.regNr)}
                    helperText={touched.regNr && errors.regNr}
                  />
                </Grid>

                {/* VAT Number */}
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label={t("billing.vatNr")}
                    name="vatNr"
                    value={values.vatNr}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.vatNr && Boolean(errors.vatNr)}
                    helperText={touched.vatNr && errors.vatNr}
                  />
                </Grid>

                {/* City */}
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label={t("billing.city")}
                    name="city"
                    value={values.city}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.city && Boolean(errors.city)}
                    helperText={touched.city && errors.city}
                  />
                </Grid>

                {/* Area Code (Zip Code) */}
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label={t("billing.zipCode")}
                    name="areaCode"
                    value={values.areaCode}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.areaCode && Boolean(errors.areaCode)}
                    helperText={touched.areaCode && errors.areaCode}
                  />
                </Grid>

                {/* Email Address */}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label={t("billing.email")}
                    name="emailAddress"
                    value={values.emailAddress}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.emailAddress && Boolean(errors.emailAddress)}
                    helperText={touched.emailAddress && errors.emailAddress}
                  />
                </Grid>
              </Grid>
              <Box mt={2}>
                <Button
                  variant="contained"
                  color="primary"
                  type="submit"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? t("common.saving") : t("common.save")}
                </Button>
              </Box>
            </Form>
          )}
        </Formik>
      </Paper>
      {/* Account Type */}
      <Paper elevation={3} sx={{ padding: 2, marginBottom: 4 }}>
        <Typography variant="h5" gutterBottom>
          {t("billing.accountType")}
        </Typography>
        <Chip
          label={accountType || "-"}
          color={accountType ? "primary" : "default"}
          variant="outlined"
          sx={{ marginTop: 1, backgroundColor: 'transparent' }}
        />
      </Paper>
      {/* Global Message Snackbar */}
      <GlobalMessage
        open={openSnackbar}
        onClose={handleCloseSnackbar}
        message={fetchError || successMessage}
        severity={fetchError ? "error" : "success"}
      />
    </Box>
  );
};

export default BillingComponent;
